import React, { useState } from "react"
import { Link, useSearchParams } from "react-router"
import { useTranslations } from "@/hooks/useTranslations"
import { Eye, EyeOff, Loader2, Lock, LogIn, ShieldCheck, AlertCircle } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useAuth as useModuleAuth } from "@optimgov/module-auth"
import { LocalRoleSelector } from "@/components/LocalRoleSelector"
import { PasswordAuthError, signInWithPassword } from "@/lib/password-session"

/** Only same-origin paths may be used as a post-login destination. */
function safeReturnUrl(v: string | null): string {
	return v && v.startsWith("/") && !v.startsWith("//") ? v : "/"
}

export default function LoginForm() {
	const t = useTranslations("login")
	const [searchParams] = useSearchParams()
	const returnUrl = safeReturnUrl(searchParams.get("returnUrl"))
	const { login, isAuthenticated } = useModuleAuth()

	const [msLoading, setMsLoading] = useState(false)
	const [pwLoading, setPwLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [showPassword, setShowPassword] = useState(false)

	// Redirect if already authenticated
	React.useEffect(() => {
		if (isAuthenticated) {
			window.location.href = returnUrl
		}
	}, [isAuthenticated, returnUrl])

	const handleAzureLogin = async () => {
		setMsLoading(true)
		setError(null)
		try {
			await login()
		} catch (err) {
			console.error("[login] Microsoft sign-in failed:", err)
			setMsLoading(false)
			setError(t("errors.generic"))
		}
	}

	const handleCredentialsLogin = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!email.trim() || !password) {
			setError(t("errors.required"))
			return
		}
		setPwLoading(true)
		setError(null)
		try {
			await signInWithPassword(email.trim(), password)
			// Full reload: the auth provider picks the stored session up on boot.
			if (window.location.pathname === "/login") window.location.href = returnUrl
			else window.location.reload()
		} catch (err) {
			setPwLoading(false)
			const code = err instanceof PasswordAuthError ? err.code : "unknown"
			if (code === "invalid_credentials") setError(t("errors.invalid"))
			else if (code === "locked") setError(t("errors.locked", { minutes: Math.max(1, Math.ceil(((err as PasswordAuthError).retryAfterSeconds ?? 900) / 60)) }))
			else if (code === "too_many_requests") setError(t("errors.tooMany"))
			else if (code === "network") setError(t("errors.network"))
			else setError(t("errors.generic"))
		}
	}

	const busy = msLoading || pwLoading
	const inputClass =
		"block w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 text-gray-700 placeholder:text-gray-400"

	return (
		<div className='w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden'>
			{/* Green Header */}
			<div className='bg-primary px-6 py-4'>
				<div className='flex items-center justify-center gap-2 text-white'>
					<Lock className='h-5 w-5' />
					<span className='font-medium'>{t("secureAccess")}</span>
				</div>
			</div>

			{/* Form Content */}
			<div className='p-6 sm:p-8'>
				<div className='text-center mb-6'>
					<h1 className='text-2xl font-bold text-gray-700 mb-2'>{t("title")}</h1>
					<p className='text-sm text-gray-400'>{t("subtitle")}</p>
				</div>

				{/* Error Message */}
				{error && (
					<div role='alert' className='relative mb-5 bg-red-50 border border-red-200 text-red-700 pl-4 pr-9 py-3 rounded-lg text-sm flex items-start gap-2'>
						<AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0' />
						<p className='flex-1'>{error}</p>
						<button
							type='button'
							onClick={() => setError(null)}
							className='absolute top-2.5 right-2.5 flex items-center justify-center w-5 h-5 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors'
							aria-label='Dismiss'
						>
							<span className='text-base leading-none'>&times;</span>
						</button>
					</div>
				)}

				{/* Local mode: role selector */}
				{import.meta.env.VITE_LOCAL_MODE === "true" ? (
					<LocalRoleSelector />
				) : (
					<>
						{/* OptimGov account: e-mail + password */}
						<form onSubmit={handleCredentialsLogin} className='space-y-4' noValidate>
							<div className='space-y-2'>
								<label htmlFor='email' className='block text-sm font-medium text-gray-500'>
									{t("email")}
								</label>
								<input
									id='email'
									type='email'
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									className={inputClass}
									placeholder={t("emailPlaceholder")}
									autoComplete='username'
									autoFocus
									disabled={busy}
								/>
							</div>

							<div className='space-y-2'>
								<div className='flex items-center justify-between'>
									<label htmlFor='password' className='block text-sm font-medium text-gray-500'>
										{t("password")}
									</label>
									<Link to='/forgot-password' className='text-sm text-primary hover:underline' tabIndex={busy ? -1 : 0}>
										{t("forgotPassword")}
									</Link>
								</div>
								<div className='relative'>
									<input
										id='password'
										type={showPassword ? "text" : "password"}
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										className={`${inputClass} pr-12`}
										placeholder={t("passwordPlaceholder")}
										autoComplete='current-password'
										disabled={busy}
									/>
									<button
										type='button'
										onClick={() => setShowPassword(!showPassword)}
										className='absolute inset-y-0 right-0 pr-4 flex items-center'
										aria-label={showPassword ? "Hide password" : "Show password"}
									>
										{showPassword ? (
											<EyeOff className='h-5 w-5 text-gray-400 hover:text-gray-600' />
										) : (
											<Eye className='h-5 w-5 text-gray-400 hover:text-gray-600' />
										)}
									</button>
								</div>
							</div>

							<Button
								type='submit'
								size='lg'
								className='w-full py-3 text-base font-semibold bg-primary hover:bg-brand-forest-start text-white'
								disabled={busy}
							>
								{pwLoading ? (
									<>
										<Loader2 className='mr-2 h-5 w-5 animate-spin' />
										{t("signingIn")}
									</>
								) : (
									<>
										<LogIn className='mr-2 h-5 w-5' />
										{t("signIn")}
									</>
								)}
							</Button>
						</form>

						{/* Divider */}
						<div className='my-6 flex items-center gap-3' aria-hidden='true'>
							<div className='h-px flex-1 bg-gray-200' />
							<span className='text-xs uppercase tracking-wide text-gray-400'>{t("or")}</span>
							<div className='h-px flex-1 bg-gray-200' />
						</div>

						{/* Microsoft (Entra External ID) account */}
						<div className='space-y-3'>
							<Button
								type='button'
								variant='outline'
								onClick={handleAzureLogin}
								disabled={busy}
								size='lg'
								className='w-full py-3 text-base font-semibold text-gray-700'
							>
								{msLoading ? (
									<>
										<Loader2 className='mr-2 h-5 w-5 animate-spin' />
										{t("connectingMicrosoft")}
									</>
								) : (
									<>
										<svg className='mr-2 h-5 w-5' viewBox='0 0 21 21' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
											<rect x='1' y='1' width='9' height='9' fill='#F25022' />
											<rect x='11' y='1' width='9' height='9' fill='#7FBA00' />
											<rect x='1' y='11' width='9' height='9' fill='#00A4EF' />
											<rect x='11' y='11' width='9' height='9' fill='#FFB900' />
										</svg>
										{t("signInMicrosoft")}
									</>
								)}
							</Button>
							<p className='text-center text-xs text-gray-400'>{t("microsoftHint")}</p>
						</div>
					</>
				)}

				{/* Secure Connection Badge */}
				<div className='mt-6 flex items-center justify-center gap-2 text-sm text-gray-400'>
					<ShieldCheck className='h-4 w-4' />
					<span>{t("secureConnection")}</span>
				</div>
			</div>
		</div>
	)
}
